import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Cube,
  Package,
  CheckCircle,
  Warning,
  ClipboardText,
  Spinner,
  ArrowDown,
  Bell,
  Clock,
  PencilSimple,
  ToggleLeft,
  ToggleRight,
  Eye,
  EyeSlash,
  ArrowSquareOut,
  ChartBar,
  CaretDown,
  CaretRight,
  FolderSimple,
  MapPin,
  FilePdf,
  Plus,
  Tag,
  Funnel
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Default Cost Tag Labels and prefixes (fallback)
const DEFAULT_COST_TAG_LABELS = {
  food: 'Food',
  beverage: 'Beverage',
  housekeeping: 'Housekeeping',
  engineering: 'Engineering',
  other: 'Other'
};

const DEFAULT_COST_TAG_PREFIX = {
  food: 'FD',
  beverage: 'BV',
  housekeeping: 'HK',
  engineering: 'EN',
  other: 'OT'
};

// Alias for backward compatibility
const COST_TAG_LABELS = DEFAULT_COST_TAG_LABELS;
const COST_TAG_PREFIX = DEFAULT_COST_TAG_PREFIX;

const COST_TAG_COLORS = {
  food: 'bg-orange-100 text-orange-700',
  beverage: 'bg-blue-100 text-blue-700',
  housekeeping: 'bg-green-100 text-green-700',
  engineering: 'bg-purple-100 text-purple-700',
  other: 'bg-slate-100 text-slate-700',
  // Default for custom tags
  custom: 'bg-violet-100 text-violet-700'
};

const StorekeeperPortalPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [countItems, setCountItems] = useState([]);
  const [countNotes, setCountNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showInactive, setShowInactive] = useState(false);
  
  // Hierarchical data state
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [organisedItems, setOrganisedItems] = useState(null);
  
  // Expandable tree state
  const [expandedLocations, setExpandedLocations] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  
  // Internal Requisition state
  const [internalReqs, setInternalReqs] = useState([]);
  const [selectedInternalReq, setSelectedInternalReq] = useState(null);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueItems, setIssueItems] = useState([]);
  const [issueNotes, setIssueNotes] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Add Item dialog state
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    item_name: '',
    location_id: '',
    category_id: '',
    subcategory_id: '',
    cost_tag: 'food',
    unit_of_measure: 'units',
    par_level: 0,
    description: ''
  });
  
  // Filtered categories/subcategories for Add Item form
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  
  // Edit Item dialog state
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFilteredCategories, setEditFilteredCategories] = useState([]);
  const [editFilteredSubcategories, setEditFilteredSubcategories] = useState([]);
  
  // PDF Export state
  const [exportFilterLocation, setExportFilterLocation] = useState('');
  const [exportFilterCategory, setExportFilterCategory] = useState('');
  const [exportFilterCostTag, setExportFilterCostTag] = useState('');
  const [exporting, setExporting] = useState(false);

  // Add Location/Category/Subcategory dialogs
  const [addLocationDialogOpen, setAddLocationDialogOpen] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: '', description: '' });
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', location_id: '', description: '' });
  const [addSubcategoryDialogOpen, setAddSubcategoryDialogOpen] = useState(false);
  const [newSubcategory, setNewSubcategory] = useState({ name: '', category_id: '', description: '' });

  // Cost Tags state (dynamic - includes both default and custom)
  const [costTags, setCostTags] = useState([]);
  const [allCostTags, setAllCostTags] = useState([]); // All tags including inactive
  const [addCostTagDialogOpen, setAddCostTagDialogOpen] = useState(false);
  const [newCostTag, setNewCostTag] = useState({ name: '', code: '' });
  const [editCostTagDialogOpen, setEditCostTagDialogOpen] = useState(false);
  const [editingCostTag, setEditingCostTag] = useState(null);

  // Replenishment Requisition state
  const [replenishDialogOpen, setReplenishDialogOpen] = useState(false);
  const [replenishItem, setReplenishItem] = useState(null);
  const [replenishQty, setReplenishQty] = useState('');
  const [replenishUrgency, setReplenishUrgency] = useState('routine');
  const [replenishNotes, setReplenishNotes] = useState('');
  
  // Helper to get cost tag label
  const getCostTagLabel = (tagValue) => {
    const tag = costTags.find(t => t.code.toLowerCase() === tagValue?.toLowerCase() || t.code === tagValue);
    return tag ? tag.name : DEFAULT_COST_TAG_LABELS[tagValue] || tagValue;
  };
  
  // Helper to get cost tag prefix
  const getCostTagPrefix = (tagValue) => {
    const tag = costTags.find(t => t.code.toLowerCase() === tagValue?.toLowerCase() || t.code === tagValue);
    return tag ? tag.code : DEFAULT_COST_TAG_PREFIX[tagValue] || 'OT';
  };
  
  // Helper to get cost tag color
  const getCostTagColor = (tagValue) => {
    const defaultColors = COST_TAG_COLORS[tagValue?.toLowerCase()];
    if (defaultColors) return defaultColors;
    return COST_TAG_COLORS.custom;
  };

  useEffect(() => {
    fetchDashboard();
    fetchInternalRequisitions();
    fetchHierarchyData();
    fetchCostTags();
  }, []);

  const fetchCostTags = async () => {
    try {
      const [activeRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/cost-tags/active`, getAuthHeaders()),
        axios.get(`${API_URL}/api/cost-tags`, getAuthHeaders())
      ]);
      setCostTags(activeRes.data);
      setAllCostTags(allRes.data);
    } catch (error) {
      console.error('Failed to fetch cost tags:', error);
      // Fall back to defaults if API fails
      setCostTags([
        { code: 'FD', name: 'Food', is_default: true },
        { code: 'BV', name: 'Beverage', is_default: true },
        { code: 'HK', name: 'Housekeeping', is_default: true },
        { code: 'EN', name: 'Engineering', is_default: true },
        { code: 'OT', name: 'Other', is_default: true }
      ]);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/storekeeper-portal/dashboard`, getAuthHeaders());
      setDashboard(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchyData = async () => {
    try {
      const [locRes, catRes, subcatRes, orgRes] = await Promise.all([
        axios.get(`${API_URL}/api/store-locations`, getAuthHeaders()),
        axios.get(`${API_URL}/api/item-categories`, getAuthHeaders()),
        axios.get(`${API_URL}/api/item-subcategories`, getAuthHeaders()),
        axios.get(`${API_URL}/api/item-master/organised`, getAuthHeaders())
      ]);
      setLocations(locRes.data);
      setCategories(catRes.data);
      setSubcategories(subcatRes.data);
      setOrganisedItems(orgRes.data);
      
      // Auto-expand first location
      if (orgRes.data?.length > 0) {
        setExpandedLocations({ [orgRes.data[0].id]: true });
      }
    } catch (error) {
      console.error('Failed to fetch hierarchy data:', error);
    }
  };

  const fetchInternalRequisitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/storekeeper-portal/internal-requisitions`, getAuthHeaders());
      setInternalReqs(res.data);
    } catch (error) {
      console.error('Failed to load internal requisitions:', error);
    }
  };

  // Cost Tag Management Functions
  const handleAddCostTag = async () => {
    if (!newCostTag.name.trim() || !newCostTag.code.trim()) {
      toast.error('Name and code are required');
      return;
    }
    if (newCostTag.code.length !== 2) {
      toast.error('Code must be exactly 2 letters');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/cost-tags`, {
        name: newCostTag.name.trim(),
        code: newCostTag.code.toUpperCase().trim()
      }, getAuthHeaders());
      toast.success(`Cost tag "${newCostTag.name}" (${newCostTag.code.toUpperCase()}) created successfully`);
      setAddCostTagDialogOpen(false);
      setNewCostTag({ name: '', code: '' });
      fetchCostTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cost tag');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCostTag = async () => {
    if (!editingCostTag) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/cost-tags/${editingCostTag.id}`, {
        name: editingCostTag.name.trim()
      }, getAuthHeaders());
      toast.success('Cost tag updated successfully');
      setEditCostTagDialogOpen(false);
      setEditingCostTag(null);
      fetchCostTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cost tag');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivateCostTag = async (tagId, tagName) => {
    if (!confirm(`Are you sure you want to deactivate "${tagName}"? Existing items will keep their tag.`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/cost-tags/${tagId}`, getAuthHeaders());
      toast.success(`Cost tag "${tagName}" deactivated`);
      fetchCostTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate cost tag');
    }
  };

  const handleReactivateCostTag = async (tagId, tagName) => {
    try {
      await axios.put(`${API_URL}/api/cost-tags/${tagId}/reactivate`, {}, getAuthHeaders());
      toast.success(`Cost tag "${tagName}" reactivated`);
      fetchCostTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate cost tag');
    }
  };

  // Filter categories when location changes in Add Item form
  useEffect(() => {
    if (newItem.location_id) {
      const filtered = categories.filter(c => c.location_id === newItem.location_id);
      setFilteredCategories(filtered);
      // Reset category and subcategory if location changed
      if (!filtered.find(c => c.id === newItem.category_id)) {
        setNewItem(prev => ({ ...prev, category_id: '', subcategory_id: '' }));
        setFilteredSubcategories([]);
      }
    } else {
      setFilteredCategories([]);
    }
  }, [newItem.location_id, categories]);

  // Filter subcategories when category changes in Add Item form
  useEffect(() => {
    if (newItem.category_id) {
      const filtered = subcategories.filter(s => s.category_id === newItem.category_id);
      setFilteredSubcategories(filtered);
      if (!filtered.find(s => s.id === newItem.subcategory_id)) {
        setNewItem(prev => ({ ...prev, subcategory_id: '' }));
      }
    } else {
      setFilteredSubcategories([]);
    }
  }, [newItem.category_id, subcategories]);

  const openCountDialog = () => {
    if (!dashboard?.items) return;
    
    const items = dashboard.items.map(item => ({
      item_id: item.id,
      item_name: item.item_name,
      counted_quantity: item.current_stock_level || 0,
      unit_of_measure: item.unit_of_measure,
      previous_stock_level: item.current_stock_level || 0,
      variance_note: ''
    }));
    setCountItems(items);
    setCountNotes('');
    setCountDialogOpen(true);
  };

  const updateCountedQuantity = (index, qty) => {
    const newItems = [...countItems];
    newItems[index].counted_quantity = parseFloat(qty) || 0;
    setCountItems(newItems);
  };

  const updateVarianceNote = (index, note) => {
    const newItems = [...countItems];
    newItems[index].variance_note = note;
    setCountItems(newItems);
  };

  const handleSubmitCount = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/storekeeper-portal/inventory-count`, {
        items: countItems,
        count_notes: countNotes
      }, getAuthHeaders());
      
      toast.success('Inventory count submitted successfully!');
      setCountDialogOpen(false);
      fetchDashboard();
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit count');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.item_name.trim()) {
      toast.error('Item name is required');
      return;
    }
    if (!newItem.location_id) {
      toast.error('Location is required');
      return;
    }
    if (!newItem.category_id) {
      toast.error('Category is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/item-master`, {
        item_name: newItem.item_name,
        location_id: newItem.location_id,
        category_id: newItem.category_id,
        subcategory_id: newItem.subcategory_id || null,
        cost_tag: newItem.cost_tag,
        unit_of_measure: newItem.unit_of_measure,
        par_level: newItem.par_level,
        description: newItem.description || null
      }, getAuthHeaders());
      const createdItem = response.data;
      toast.success(`Item saved successfully. SKU assigned: ${createdItem.sku}`, {
        duration: 5000
      });
      setAddItemDialogOpen(false);
      setNewItem({
        item_name: '',
        location_id: '',
        category_id: '',
        subcategory_id: '',
        cost_tag: 'food',
        unit_of_measure: 'units',
        par_level: 0,
        description: ''
      });
      fetchDashboard();
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (item) => {
    // Find categories for this item's location
    const itemCats = categories.filter(c => c.location_id === item.location_id);
    setEditFilteredCategories(itemCats);
    
    // Find subcategories for this item's category
    const itemSubcats = subcategories.filter(s => s.category_id === item.category_id);
    setEditFilteredSubcategories(itemSubcats);
    
    setEditingItem({
      id: item.id,
      item_name: item.item_name,
      location_id: item.location_id || '',
      category_id: item.category_id || '',
      subcategory_id: item.subcategory_id || '',
      cost_tag: item.cost_tag || 'other',
      original_cost_tag: item.cost_tag || 'other', // Store original for comparison
      unit_of_measure: item.unit_of_measure,
      par_level: item.par_level || 0,
      description: item.description || '',
      is_active: item.is_active !== false,
      sku: item.sku
    });
    setEditItemDialogOpen(true);
  };

  const handleEditItem = async () => {
    if (!editingItem?.item_name?.trim()) {
      toast.error('Item name is required');
      return;
    }
    
    const oldSku = editingItem.sku;
    const oldCostTag = editingItem.original_cost_tag; // Store original cost tag
    const newCostTag = editingItem.cost_tag;
    const isCostTagChanged = oldCostTag && newCostTag !== oldCostTag;
    
    setSubmitting(true);
    try {
      // Build update payload
      const updatePayload = {
        item_name: editingItem.item_name,
        location_id: editingItem.location_id,
        category_id: editingItem.category_id,
        subcategory_id: editingItem.subcategory_id || null,
        unit_of_measure: editingItem.unit_of_measure,
        par_level: editingItem.par_level,
        description: editingItem.description,
        is_active: editingItem.is_active
      };
      
      // If cost tag changed, include it with a reason
      if (isCostTagChanged) {
        updatePayload.cost_tag = newCostTag;
        updatePayload.cost_tag_change_reason = 'intentional_recategorisation';
      }
      
      const response = await axios.put(`${API_URL}/api/item-master/${editingItem.id}`, updatePayload, getAuthHeaders());
      const updatedItem = response.data;
      
      if (isCostTagChanged && updatedItem.sku !== oldSku) {
        toast.success(`Item updated. SKU reassigned from ${oldSku} to ${updatedItem.sku}. Audit trail recorded.`, {
          duration: 6000
        });
      } else {
        toast.success('Item updated successfully!');
      }
      
      setEditItemDialogOpen(false);
      setEditingItem(null);
      fetchDashboard();
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (item) => {
    const newStatus = item.is_active === false ? true : false;
    try {
      await axios.put(`${API_URL}/api/item-master/${item.id}`, {
        is_active: newStatus
      }, getAuthHeaders());
      toast.success(newStatus ? 'Item reactivated!' : 'Item marked as inactive');
      fetchDashboard();
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item status');
    }
  };

  // Handle editing item's location change
  useEffect(() => {
    if (editingItem?.location_id) {
      const filtered = categories.filter(c => c.location_id === editingItem.location_id);
      setEditFilteredCategories(filtered);
    }
  }, [editingItem?.location_id, categories]);

  useEffect(() => {
    if (editingItem?.category_id) {
      const filtered = subcategories.filter(s => s.category_id === editingItem.category_id);
      setEditFilteredSubcategories(filtered);
    }
  }, [editingItem?.category_id, subcategories]);

  // PDF Export
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFilterLocation) params.append('location_id', exportFilterLocation);
      if (exportFilterCategory) params.append('category_id', exportFilterCategory);
      if (exportFilterCostTag) params.append('cost_tag', exportFilterCostTag);
      
      const response = await axios.get(
        `${API_URL}/api/item-master/export-pdf${params.toString() ? '?' + params.toString() : ''}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `item_master_list_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Add Location
  const handleAddLocation = async () => {
    if (!newLocation.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/store-locations`, newLocation, getAuthHeaders());
      toast.success(`Location "${response.data.name}" saved successfully!`, { duration: 4000 });
      setAddLocationDialogOpen(false);
      setNewLocation({ name: '', description: '' });
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSubmitting(false);
    }
  };

  // Add Category
  const handleAddCategory = async () => {
    if (!newCategory.name.trim() || !newCategory.location_id) {
      toast.error('Category name and location are required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/item-categories`, newCategory, getAuthHeaders());
      toast.success(`Category "${response.data.name}" saved successfully!`, { duration: 4000 });
      setAddCategoryDialogOpen(false);
      setNewCategory({ name: '', location_id: '', description: '' });
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  // Add Subcategory
  const handleAddSubcategory = async () => {
    if (!newSubcategory.name.trim() || !newSubcategory.category_id) {
      toast.error('Subcategory name and category are required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/item-subcategories`, newSubcategory, getAuthHeaders());
      toast.success(`Subcategory "${response.data.name}" saved successfully!`, { duration: 4000 });
      setAddSubcategoryDialogOpen(false);
      setNewSubcategory({ name: '', category_id: '', description: '' });
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subcategory');
    } finally {
      setSubmitting(false);
    }
  };

  // Internal Requisition functions
  const openIssueDialog = (req) => {
    setSelectedInternalReq(req);
    setIssueItems(req.items.map(item => ({
      ...item,
      quantity_issued: Math.min(item.quantity_requested, item.current_stock || 0),
      shortfall_note: ''
    })));
    setIssueNotes('');
    setIssueDialogOpen(true);
  };

  const handleIssueRequisition = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/internal-requisitions/${selectedInternalReq.id}/issue`, {
        items: issueItems,
        issue_notes: issueNotes
      }, getAuthHeaders());
      toast.success('Items issued successfully! Inventory updated.');
      setIssueDialogOpen(false);
      setSelectedInternalReq(null);
      fetchInternalRequisitions();
      fetchDashboard();
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue items');
    } finally {
      setSubmitting(false);
    }
  };

  const openRejectDialog = (req) => {
    setSelectedInternalReq(req);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectRequisition = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/internal-requisitions/${selectedInternalReq.id}/reject`, {
        rejection_reason: rejectReason
      }, getAuthHeaders());
      toast.success('Requisition rejected');
      setRejectDialogOpen(false);
      setSelectedInternalReq(null);
      fetchInternalRequisitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject requisition');
    } finally {
      setSubmitting(false);
    }
  };

  const updateIssueQty = (itemId, qty) => {
    setIssueItems(issueItems.map(item => 
      item.item_id === itemId ? { ...item, quantity_issued: parseInt(qty) || 0 } : item
    ));
  };

  const updateShortfallNote = (itemId, note) => {
    setIssueItems(issueItems.map(item => 
      item.item_id === itemId ? { ...item, shortfall_note: note } : item
    ));
  };

  const toggleLocation = (locId) => {
    setExpandedLocations(prev => ({ ...prev, [locId]: !prev[locId] }));
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleSubcategory = (subcatId) => {
    setExpandedSubcategories(prev => ({ ...prev, [subcatId]: !prev[subcatId] }));
  };

  // Replenishment Requisition handlers
  const openReplenishDialog = (item) => {
    const deficit = Math.max(0, (item.par_level || 0) - (item.current_stock_level || 0));
    setReplenishItem(item);
    setReplenishQty(String(deficit || 1));
    setReplenishUrgency(deficit > 0 ? 'urgent' : 'routine');
    setReplenishNotes('');
    setReplenishDialogOpen(true);
  };

  const handleSubmitReplenishment = async () => {
    if (!replenishItem || !replenishQty || parseFloat(replenishQty) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/replenishment-requisitions`, {
        item_id: replenishItem.id,
        item_name: replenishItem.item_name,
        sku: replenishItem.sku,
        location_name: replenishItem.location_name || locations.find(l => l.id === replenishItem.location_id)?.name || '',
        category_name: replenishItem.category_name || categories.find(c => c.id === replenishItem.category_id)?.name || '',
        unit_of_measure: replenishItem.unit_of_measure,
        current_stock: replenishItem.current_stock_level || 0,
        par_level: replenishItem.par_level || 0,
        quantity_to_replenish: parseFloat(replenishQty),
        urgency_level: replenishUrgency,
        notes: replenishNotes
      }, getAuthHeaders());
      toast.success('Replenishment requisition submitted to PM!');
      setReplenishDialogOpen(false);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const pendingInternalReqs = internalReqs.filter(r => ['submitted', 'in_progress'].includes(r.status));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8B5CF6] border-t-transparent"></div>
      </div>
    );
  }

  // Render hierarchical item display
  const renderItemRow = (item, canEditItem = false) => {
    const belowPar = (item.current_stock_level || 0) < (item.par_level || 0);
    const isInactive = item.is_active === false;
    
    if (!showInactive && isInactive) return null;
    
    return (
      <tr 
        key={item.id} 
        className={`hover:bg-slate-50/50 ${belowPar ? 'bg-red-50' : ''} ${isInactive ? 'opacity-50' : ''}`}
        data-testid={`item-row-${item.id}`}
      >
        <td className="px-3 py-2">
          <span className="font-mono text-[#8B5CF6] font-semibold text-sm">{item.sku || 'LEGACY'}</span>
        </td>
        <td className="px-3 py-2 font-medium text-slate-800">{item.item_name}</td>
        <td className="px-3 py-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COST_TAG_COLORS[item.cost_tag] || COST_TAG_COLORS.other}`}>
            {COST_TAG_LABELS[item.cost_tag] || item.cost_tag}
          </span>
        </td>
        <td className="px-3 py-2 text-slate-600 text-sm">{item.unit_of_measure}</td>
        <td className="px-3 py-2 text-right font-medium">{(item.par_level || 0).toFixed(1)}</td>
        <td className={`px-3 py-2 text-right font-medium ${belowPar ? 'text-red-600' : 'text-green-600'}`}>
          {(item.current_stock_level || 0).toFixed(1)}
        </td>
        <td className="px-3 py-2 text-center">
          {belowPar ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <ArrowDown size={10} className="mr-1" />
              Below Par
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              OK
            </span>
          )}
        </td>
        {canEditItem && (
          <td className="px-3 py-2">
            <div className="flex items-center justify-center gap-1">
              {user?.role === 'storekeeper' && (
                <button
                  onClick={() => openReplenishDialog(item)}
                  className="p-1.5 rounded-lg hover:bg-[#14B8A6]/10 text-[#14B8A6] hover:text-[#0D9488] transition-colors"
                  title="Create Replenishment Requisition"
                  data-testid={`replenish-item-${item.id}`}
                >
                  <Plus size={16} />
                </button>
              )}
              <button
                onClick={() => openEditDialog(item)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-[#8B5CF6] transition-colors"
                title="Edit item"
                data-testid={`edit-item-${item.id}`}
              >
                <PencilSimple size={16} />
              </button>
              <button
                onClick={() => handleToggleActive(item)}
                className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${isInactive ? 'text-green-600 hover:text-green-700' : 'text-amber-600 hover:text-amber-700'}`}
                title={isInactive ? 'Reactivate item' : 'Mark as inactive'}
                data-testid={`toggle-item-${item.id}`}
              >
                {isInactive ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
              </button>
            </div>
          </td>
        )}
      </tr>
    );
  };

  // Determine page title based on role
  const isPM = ['purchasing_manager', 'system_admin'].includes(user?.role);
  const canEdit = isPM; // Only PM can edit Item Master List
  const pageTitle = isPM ? 'Item Master & Inventory' : 'Storekeeper Portal';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
            <Cube size={24} className="text-[#8B5CF6]" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {pageTitle}
            </h1>
            <p className="text-slate-500">Welcome, {user?.full_name}</p>
          </div>
        </div>
      </div>

      {/* Key Actions - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          onClick={openCountDialog}
          className="bg-gradient-to-br from-[#8B5CF6] to-[#A78BFA] text-white rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
          data-testid="action-manage-stock"
        >
          <Package size={32} className="mb-3" />
          <h3 className="text-xl font-bold">Manage Stock</h3>
          <p className="text-white/80 text-sm mt-1">{dashboard?.total_items || 0} items, {dashboard?.items_below_par || 0} below par</p>
        </div>
        <div 
          onClick={() => setActiveTab('internal-reqs')}
          className="bg-white rounded-xl border border-slate-100 p-6 cursor-pointer hover:shadow-lg transition-all"
          data-testid="action-issue-items"
        >
          <ArrowSquareOut size={32} className="text-[#8B5CF6] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">Issue Items</h3>
          <p className="text-slate-500 text-sm mt-1">{pendingInternalReqs.length} internal requests pending</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'overview' 
              ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-overview"
        >
          Stock Overview
        </button>
        <button
          onClick={() => setActiveTab('internal-reqs')}
          className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
            activeTab === 'internal-reqs' 
              ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-internal-reqs"
        >
          Internal Requisitions
          {pendingInternalReqs.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingInternalReqs.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('master')}
          className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'master' 
              ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-master-list"
        >
          Item Master List
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'alerts' 
              ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-alerts"
        >
          Par Level Alerts ({dashboard?.active_alerts?.length || 0})
        </button>
        {user?.role === 'storekeeper' && (
          <button
            onClick={() => setActiveTab('replenishment')}
            className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'replenishment' 
                ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-replenishment"
          >
            Replenishment Reqs ({dashboard?.replenishment_requisitions?.length || 0})
          </button>
        )}
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'history' 
              ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Count History
        </button>
        {isPM && (
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'settings' 
                ? 'text-[#8B5CF6] border-b-2 border-[#8B5CF6]' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-settings"
          >
            Settings
          </button>
        )}
      </div>

      {/* Stock Overview Tab - Now with hierarchical display */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Current Stock Levels (Hierarchical View)</h2>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-slate-300 text-[#8B5CF6] focus:ring-[#8B5CF6]"
              />
              Show Inactive
            </label>
          </div>
          
          {!organisedItems?.length ? (
            <div className="text-center py-12 text-slate-500">
              <Package size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No items in inventory</p>
              <p className="text-sm mt-2">Start by adding locations, categories, and items</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {organisedItems.map(location => (
                <div key={location.id} className="border-b border-slate-100 last:border-b-0">
                  {/* Location Header */}
                  <button
                    onClick={() => toggleLocation(location.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#8B5CF6]/10 to-transparent hover:from-[#8B5CF6]/20 transition-colors"
                    data-testid={`location-${location.id}`}
                  >
                    {expandedLocations[location.id] ? (
                      <CaretDown size={18} className="text-[#8B5CF6]" />
                    ) : (
                      <CaretRight size={18} className="text-[#8B5CF6]" />
                    )}
                    <MapPin size={20} className="text-[#8B5CF6]" />
                    <span className="font-semibold text-slate-800">{location.name}</span>
                    <span className="text-xs text-slate-500 ml-auto">
                      {location.categories?.reduce((acc, cat) => 
                        acc + (cat.items?.length || 0) + cat.subcategories?.reduce((s, sub) => s + (sub.items?.length || 0), 0), 0
                      ) || 0} items
                    </span>
                  </button>
                  
                  {expandedLocations[location.id] && (
                    <div className="pl-4">
                      {location.categories?.map(category => (
                        <div key={category.id} className="border-l-2 border-slate-200 ml-4">
                          {/* Category Header */}
                          <button
                            onClick={() => toggleCategory(category.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
                            data-testid={`category-${category.id}`}
                          >
                            {expandedCategories[category.id] ? (
                              <CaretDown size={16} className="text-slate-500" />
                            ) : (
                              <CaretRight size={16} className="text-slate-500" />
                            )}
                            <FolderSimple size={18} className="text-amber-500" />
                            <span className="font-medium text-slate-700">{category.name}</span>
                            <span className="text-xs text-slate-400 ml-auto">
                              {(category.items?.length || 0) + category.subcategories?.reduce((s, sub) => s + (sub.items?.length || 0), 0)} items
                            </span>
                          </button>
                          
                          {expandedCategories[category.id] && (
                            <div className="pl-4">
                              {/* Items directly in category (no subcategory) */}
                              {category.items?.length > 0 && (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                      <tr>
                                        <th className="px-3 py-2 text-left">SKU</th>
                                        <th className="px-3 py-2 text-left">Item</th>
                                        <th className="px-3 py-2 text-left">Cost Tag</th>
                                        <th className="px-3 py-2 text-left">Unit</th>
                                        <th className="px-3 py-2 text-right">Par</th>
                                        <th className="px-3 py-2 text-right">Stock</th>
                                        <th className="px-3 py-2 text-center">Status</th>
                                        {isPM && <th className="px-3 py-2 text-center">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {category.items.map(item => renderItemRow(item, isPM))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              
                              {/* Subcategories */}
                              {category.subcategories?.map(subcategory => (
                                <div key={subcategory.id} className="border-l-2 border-slate-100 ml-4">
                                  <button
                                    onClick={() => toggleSubcategory(subcategory.id)}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
                                    data-testid={`subcategory-${subcategory.id}`}
                                  >
                                    {expandedSubcategories[subcategory.id] ? (
                                      <CaretDown size={14} className="text-slate-400" />
                                    ) : (
                                      <CaretRight size={14} className="text-slate-400" />
                                    )}
                                    <FolderSimple size={16} className="text-slate-400" />
                                    <span className="text-slate-600">{subcategory.name}</span>
                                    <span className="text-xs text-slate-400 ml-auto">{subcategory.items?.length || 0} items</span>
                                  </button>
                                  
                                  {expandedSubcategories[subcategory.id] && subcategory.items?.length > 0 && (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                          <tr>
                                            <th className="px-3 py-2 text-left">SKU</th>
                                            <th className="px-3 py-2 text-left">Item</th>
                                            <th className="px-3 py-2 text-left">Cost Tag</th>
                                            <th className="px-3 py-2 text-left">Unit</th>
                                            <th className="px-3 py-2 text-right">Par</th>
                                            <th className="px-3 py-2 text-right">Stock</th>
                                            <th className="px-3 py-2 text-center">Status</th>
                                            {isPM && <th className="px-3 py-2 text-center">Actions</th>}
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                          {subcategory.items.map(item => renderItemRow(item, isPM))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Item Master List Tab - Now with hierarchy management */}
      {activeTab === 'master' && (
        <div className="space-y-4">
          {/* Setup Guidance for New Users - ONLY FOR PMs */}
          {isPM && locations.length === 0 && (
            <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#A78BFA]/10 rounded-xl p-6 border border-[#8B5CF6]/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} className="text-[#8B5CF6]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Welcome to Item Master Setup</h3>
                  <p className="text-slate-600 mb-4">
                    To get started, follow these steps in order:
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#8B5CF6]/30">
                      <span className="w-6 h-6 rounded-full bg-[#8B5CF6] text-white text-sm font-bold flex items-center justify-center">1</span>
                      <MapPin size={18} className="text-[#8B5CF6]" />
                      <span className="font-medium text-slate-700">Create a Location</span>
                      <span className="text-sm text-slate-500 ml-auto">Required first</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 opacity-60">
                      <span className="w-6 h-6 rounded-full bg-slate-400 text-white text-sm font-bold flex items-center justify-center">2</span>
                      <FolderSimple size={18} className="text-slate-400" />
                      <span className="font-medium text-slate-500">Create a Category</span>
                      <span className="text-sm text-slate-400 ml-auto">After location</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 opacity-60">
                      <span className="w-6 h-6 rounded-full bg-slate-400 text-white text-sm font-bold flex items-center justify-center">3</span>
                      <Package size={18} className="text-slate-400" />
                      <span className="font-medium text-slate-500">Add Items</span>
                      <span className="text-sm text-slate-400 ml-auto">After category</span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setAddLocationDialogOpen(true)}
                    className="mt-4 bg-[#8B5CF6] hover:bg-[#7C3AED]"
                    data-testid="setup-add-location-btn"
                  >
                    <MapPin size={16} className="mr-2" />
                    Start: Add Your First Location
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 Guidance - Has locations but no categories - ONLY FOR PMs */}
          {isPM && locations.length > 0 && categories.length === 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <FolderSimple size={24} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Step 2: Create a Category</h3>
                  <p className="text-slate-600 mb-4">
                    Great! You have {locations.length} location{locations.length > 1 ? 's' : ''}. Now create a category within a location to organize your items.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-3 p-2 bg-white/50 rounded-lg">
                      <CheckCircle size={18} className="text-green-600" />
                      <span className="text-slate-600">Location created</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-amber-300">
                      <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">2</span>
                      <FolderSimple size={18} className="text-amber-600" />
                      <span className="font-medium text-slate-700">Create a Category</span>
                      <span className="text-sm text-amber-600 ml-auto">Your next step</span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setAddCategoryDialogOpen(true)}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="setup-add-category-btn"
                  >
                    <FolderSimple size={16} className="mr-2" />
                    Next: Add Your First Category
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons - Show based on setup state - ONLY FOR PMs */}
          {isPM && (
            <div className="flex flex-wrap items-center gap-3">
              {/* Add Location - Always available */}
              <Button 
                onClick={() => setAddLocationDialogOpen(true)}
                variant={locations.length === 0 ? "default" : "outline"}
                className={locations.length === 0 ? "bg-[#8B5CF6] hover:bg-[#7C3AED]" : ""}
                data-testid="add-location-btn"
              >
                <MapPin size={16} className="mr-2" />
                Add Location
                {locations.length === 0 && <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded">Start Here</span>}
              </Button>

              {/* Add Category - Only after locations exist */}
              <Button 
                onClick={() => setAddCategoryDialogOpen(true)}
                variant={locations.length > 0 && categories.length === 0 ? "default" : "outline"}
                className={locations.length > 0 && categories.length === 0 ? "bg-amber-500 hover:bg-amber-600" : ""}
                disabled={locations.length === 0}
                data-testid="add-category-btn"
                title={locations.length === 0 ? "Create a location first" : "Add a new category"}
              >
                <FolderSimple size={16} className="mr-2" />
                Add Category
                {locations.length === 0 && <span className="ml-2 text-xs text-slate-400">(Need location first)</span>}
              </Button>

              {/* Add Subcategory - Only after categories exist */}
              <Button 
                onClick={() => setAddSubcategoryDialogOpen(true)}
                variant="outline"
                disabled={categories.length === 0}
                data-testid="add-subcategory-btn"
                title={categories.length === 0 ? "Create a category first" : "Add a subcategory (optional)"}
              >
                <Plus size={16} className="mr-2" />
                Add Subcategory
                {categories.length === 0 && <span className="ml-2 text-xs text-slate-400">(Optional)</span>}
              </Button>

              {/* Add New Item - Only after both locations AND categories exist */}
              <Button 
                onClick={() => setAddItemDialogOpen(true)}
                className={locations.length > 0 && categories.length > 0 ? "bg-[#8B5CF6] hover:bg-[#7C3AED]" : ""}
                disabled={locations.length === 0 || categories.length === 0}
                data-testid="add-item-btn"
                title={locations.length === 0 ? "Create a location first" : categories.length === 0 ? "Create a category first" : "Add a new item"}
              >
                <Package size={16} className="mr-2" />
                Add New Item
                {(locations.length === 0 || categories.length === 0) && (
                  <span className="ml-2 text-xs text-slate-400">
                    ({locations.length === 0 ? "Need location" : "Need category"})
                  </span>
                )}
              </Button>
            </div>
          )}
          
          {/* PDF Export Card */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <FilePdf size={24} className="text-red-500" />
                <span className="font-medium text-slate-700">Export Item Master List</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <select
                  value={exportFilterLocation}
                  onChange={(e) => setExportFilterLocation(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg"
                  data-testid="export-filter-location"
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                <select
                  value={exportFilterCategory}
                  onChange={(e) => setExportFilterCategory(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg"
                  data-testid="export-filter-category"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <select
                  value={exportFilterCostTag}
                  onChange={(e) => setExportFilterCostTag(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg"
                  data-testid="export-filter-cost-tag"
                >
                  <option value="">All Cost Tags</option>
                  {Object.entries(COST_TAG_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <Button 
                onClick={handleExportPDF}
                disabled={exporting}
                className="bg-red-500 hover:bg-red-600"
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <Spinner size={16} className="animate-spin mr-2" />
                ) : (
                  <FilePdf size={16} className="mr-2" />
                )}
                Export PDF
              </Button>
            </div>
          </div>

          {/* Master List Table - Flat View */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Item Master List</h2>
                <p className="text-sm text-slate-500">All items with SKUs, organized by hierarchy</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-slate-300 text-[#8B5CF6] focus:ring-[#8B5CF6]"
                />
                Show Inactive
              </label>
            </div>
            {!dashboard?.items?.length ? (
              <div className="text-center py-12 text-slate-500">
                <Package size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No items in master list</p>
                <p className="text-sm">Click "Add New Item" to start building your inventory</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="master-list-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Item Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost Tag</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Par Level</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Stock</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      {isPM && <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dashboard.items
                      .filter(item => showInactive || item.is_active !== false)
                      .map(item => {
                        const isInactive = item.is_active === false;
                        const belowPar = (item.current_stock_level || 0) < (item.par_level || 0);
                        return (
                          <tr key={item.id} className={`hover:bg-slate-50/50 ${isInactive ? 'bg-slate-50 opacity-60' : ''}`}>
                            <td className="px-4 py-3">
                              <span className="font-mono text-[#8B5CF6] font-bold">{item.sku || 'LEGACY'}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">{item.item_name}</div>
                              {item.subcategory_name && (
                                <div className="text-xs text-slate-500">{item.subcategory_name}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-600 text-sm">
                              <span className="flex items-center gap-1">
                                <MapPin size={12} className="text-[#8B5CF6]" />
                                {item.location_name || 'Unassigned'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600 text-sm">
                              <span className="flex items-center gap-1">
                                <FolderSimple size={12} className="text-amber-500" />
                                {item.category_name || 'Uncategorized'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${COST_TAG_COLORS[item.cost_tag] || COST_TAG_COLORS.other}`}>
                                {COST_TAG_LABELS[item.cost_tag] || item.cost_tag}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{item.unit_of_measure}</td>
                            <td className="px-4 py-3 text-right font-medium text-slate-800">
                              {(item.par_level || 0).toFixed(1)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-medium ${belowPar ? 'text-red-600' : 'text-green-600'}`}>
                                {(item.current_stock_level || 0).toFixed(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isInactive ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                                  <EyeSlash size={12} className="mr-1" />
                                  Inactive
                                </span>
                              ) : belowPar ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  <ArrowDown size={12} className="mr-1" />
                                  Below Par
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  <CheckCircle size={12} className="mr-1" />
                                  OK
                                </span>
                              )}
                            </td>
                            {isPM && (
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => openEditDialog(item)}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-[#8B5CF6] transition-colors"
                                    title="Edit item"
                                    data-testid={`edit-item-${item.id}`}
                                  >
                                    <PencilSimple size={18} />
                                  </button>
                                  <button
                                    onClick={() => handleToggleActive(item)}
                                    className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${isInactive ? 'text-green-600 hover:text-green-700' : 'text-amber-600 hover:text-amber-700'}`}
                                    title={isInactive ? 'Reactivate item' : 'Mark as inactive'}
                                    data-testid={`toggle-item-${item.id}`}
                                  >
                                    {isInactive ? <ToggleLeft size={18} /> : <ToggleRight size={18} />}
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-amber-50">
            <h2 className="text-lg font-semibold text-slate-800">Par Level Alerts</h2>
            <p className="text-sm text-slate-500">Items that need restocking</p>
          </div>
          {!dashboard?.active_alerts?.length ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
              <p>No active alerts</p>
              <p className="text-sm mt-2">All items are at or above par level!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {dashboard.active_alerts.map(alert => (
                <div key={alert.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <Warning size={20} className="text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{alert.item_name}</p>
                      <p className="text-sm text-slate-500">
                        Current: {alert.current_stock_level} {alert.unit_of_measure} | 
                        Par: {alert.par_level} {alert.unit_of_measure}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-amber-600">
                      Reorder: {alert.suggested_reorder_quantity} {alert.unit_of_measure}
                    </p>
                    <p className="text-xs text-slate-500">
                      {format(new Date(alert.created_at), 'MMM d, h:mm a')}
                    </p>
                    <Button size="sm" className="mt-2 bg-[#14B8A6] hover:bg-[#0D9488]"
                      onClick={() => openReplenishDialog({
                        id: alert.item_id, item_name: alert.item_name, sku: alert.sku,
                        unit_of_measure: alert.unit_of_measure, current_stock_level: alert.current_stock_level,
                        par_level: alert.par_level, location_id: alert.location_id, category_id: alert.category_id
                      })}
                      data-testid={`replenish-alert-btn-${alert.item_id || alert.id}`}>
                      <Plus size={14} className="mr-1" /> Replenish
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Replenishment Requisitions Tab */}
      {activeTab === 'replenishment' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="replenishment-section">
          <div className="p-4 border-b border-slate-100 bg-[#14B8A6]/5">
            <h2 className="text-lg font-semibold text-slate-800">My Replenishment Requisitions</h2>
            <p className="text-sm text-slate-500">Stock replenishment requests submitted to PM</p>
          </div>
          {!dashboard?.replenishment_requisitions?.length ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
              <p>No replenishment requisitions submitted yet</p>
              <p className="text-sm mt-2">Create one from Par Level Alerts or Item Master</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="replenishment-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Current</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Par</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Requested</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Urgency</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dashboard.replenishment_requisitions.map(req => (
                    <tr key={req.id} className="hover:bg-slate-50/50" data-testid={`replenish-row-${req.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{req.item_name}</p>
                        <p className="text-xs text-slate-500">{req.sku} | {req.location_name}</p>
                      </td>
                      <td className="px-4 py-3 text-center text-red-600 font-medium">{req.current_stock}</td>
                      <td className="px-4 py-3 text-center">{req.par_level}</td>
                      <td className="px-4 py-3 text-center font-medium">{req.quantity_to_replenish} {req.unit_of_measure}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          req.urgency_level === 'critical' ? 'bg-red-100 text-red-700' :
                          req.urgency_level === 'urgent' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>{req.urgency_level?.charAt(0).toUpperCase() + req.urgency_level?.slice(1)}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          req.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                          req.status === 'converted_to_po' ? 'bg-green-100 text-green-700' :
                          req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-slate-100'
                        }`} data-testid={`replenish-status-${req.id}`}>
                          {req.status === 'pending_review' ? 'Pending Review' :
                           req.status === 'converted_to_po' ? 'Converted to PO' :
                           req.status === 'rejected' ? 'Rejected' : req.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {req.status === 'rejected' && req.reject_reason && (
                          <p className="text-xs text-red-600" data-testid={`reject-reason-${req.id}`}>
                            Reason: {req.reject_reason}
                          </p>
                        )}
                        {req.linked_po_number && (
                          <p className="text-xs text-green-600">PO: {req.linked_po_number}</p>
                        )}
                        {req.notes && <p className="text-xs text-slate-500">{req.notes}</p>}
                        <p className="text-xs text-slate-400 mt-1">
                          {req.created_at ? format(new Date(req.created_at), 'MMM d, yyyy h:mm a') : ''}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* Count History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800">Count History</h2>
          </div>
          {!dashboard?.recent_counts?.length ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardText size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No counts submitted yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {dashboard.recent_counts.map(count => (
                <div key={count.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-800">
                        Count by {count.counted_by_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {format(new Date(count.count_date), 'MMMM d, yyyy \'at\' h:mm a')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">
                        {count.total_items_counted} items counted
                      </p>
                      {count.items_with_variance > 0 && (
                        <p className="text-sm text-amber-600">
                          {count.items_with_variance} variances
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Internal Requisitions Tab */}
      {activeTab === 'internal-reqs' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="internal-reqs-tab">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Internal Requisitions Queue</h2>
              <p className="text-sm text-slate-500">{pendingInternalReqs.length} pending requests from departments</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchInternalRequisitions}>
              Refresh
            </Button>
          </div>

          {internalReqs.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Package size={48} className="mx-auto mb-3 text-slate-300" />
              <p>No internal requisitions</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {internalReqs.map(req => (
                <div key={req.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`internal-req-${req.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-[#8B5CF6]">{req.requisition_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          req.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                          req.status === 'in_progress' ? 'bg-purple-100 text-purple-700' :
                          req.status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                          req.status === 'partially_fulfilled' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {req.status === 'submitted' ? 'Pending' : 
                           req.status === 'in_progress' ? 'In Progress' :
                           req.status === 'fulfilled' ? 'Fulfilled' :
                           req.status === 'partially_fulfilled' ? 'Partially Fulfilled' : 'Rejected'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">
                        <span className="font-medium">{req.department}</span> • Requested by {req.requester_name}
                      </p>
                      <p className="text-xs text-slate-400">
                        Required: {format(new Date(req.required_date), 'MMM d, yyyy')} • Submitted: {format(new Date(req.created_at), 'MMM d, yyyy')}
                      </p>
                      
                      {/* Items table */}
                      <div className="mt-3 bg-slate-50 rounded-lg p-3">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 uppercase">
                              <th className="pb-2">Item</th>
                              <th className="pb-2 text-center">Requested</th>
                              <th className="pb-2 text-center">In Stock</th>
                              <th className="pb-2 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {req.items?.map((item, idx) => (
                              <tr key={idx} className="border-t border-slate-200">
                                <td className="py-2 font-medium">{item.item_name}</td>
                                <td className="py-2 text-center">{item.quantity_requested} {item.unit}</td>
                                <td className="py-2 text-center">
                                  <span className={item.current_stock >= item.quantity_requested ? 'text-green-600' : 'text-red-600'}>
                                    {item.current_stock || 0}
                                  </span>
                                </td>
                                <td className="py-2 text-center">
                                  {item.quantity_issued !== null && item.quantity_issued !== undefined ? (
                                    <span className={item.quantity_issued < item.quantity_requested ? 'text-amber-600' : 'text-green-600'}>
                                      Issued: {item.quantity_issued}
                                    </span>
                                  ) : (
                                    <span className={item.current_stock >= item.quantity_requested ? 'text-green-600' : 'text-amber-600'}>
                                      {item.current_stock >= item.quantity_requested ? 'Available' : 'Insufficient'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {req.notes && (
                        <p className="text-sm text-slate-500 mt-2 italic">Note: {req.notes}</p>
                      )}
                      {req.rejection_reason && (
                        <p className="text-sm text-red-600 mt-2">Rejected: {req.rejection_reason}</p>
                      )}
                    </div>
                    
                    {/* Action buttons for pending requisitions */}
                    {['submitted', 'in_progress'].includes(req.status) && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openIssueDialog(req)}
                          data-testid={`issue-btn-${req.id}`}
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Issue
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => openRejectDialog(req)}
                          data-testid={`reject-btn-${req.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab - Cost Tag Management */}
      {activeTab === 'settings' && isPM && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Cost Tag Settings</h2>
              <p className="text-sm text-slate-500">Manage cost tags for SKU generation and reporting</p>
            </div>
            <Button
              onClick={() => setAddCostTagDialogOpen(true)}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
              data-testid="add-cost-tag-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Custom Cost Tag
            </Button>
          </div>
          
          <div className="p-4">
            {/* Default Cost Tags */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                <Tag size={16} />
                Default Cost Tags
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allCostTags.filter(t => t.is_default).map(tag => (
                  <div key={tag.code} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCostTagColor(tag.code.toLowerCase())}`}>
                        {tag.code}
                      </span>
                      <span className="font-medium text-slate-800">{tag.name}</span>
                    </div>
                    <span className="text-xs text-slate-400">Default</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Custom Cost Tags */}
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-3 flex items-center gap-2">
                <Tag size={16} />
                Custom Cost Tags
              </h3>
              {allCostTags.filter(t => !t.is_default).length === 0 ? (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <Tag size={32} className="mx-auto mb-2 text-slate-300" />
                  <p>No custom cost tags yet</p>
                  <p className="text-sm">Click "Add Custom Cost Tag" to create one</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {allCostTags.filter(t => !t.is_default).map(tag => (
                    <div 
                      key={tag.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        tag.is_active 
                          ? 'bg-white border-slate-200' 
                          : 'bg-slate-100 border-slate-200 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${COST_TAG_COLORS.custom}`}>
                          {tag.code}
                        </span>
                        <div>
                          <span className="font-medium text-slate-800">{tag.name}</span>
                          {!tag.is_active && (
                            <span className="ml-2 text-xs text-red-500">(Inactive)</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingCostTag(tag);
                            setEditCostTagDialogOpen(true);
                          }}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#8B5CF6]"
                          title="Edit"
                          data-testid={`edit-cost-tag-${tag.code}`}
                        >
                          <PencilSimple size={16} />
                        </button>
                        {tag.is_active ? (
                          <button
                            onClick={() => handleDeactivateCostTag(tag.id, tag.name)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-amber-500 hover:text-amber-600"
                            title="Deactivate"
                            data-testid={`deactivate-cost-tag-${tag.code}`}
                          >
                            <ToggleRight size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivateCostTag(tag.id, tag.name)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-green-500 hover:text-green-600"
                            title="Reactivate"
                            data-testid={`reactivate-cost-tag-${tag.code}`}
                          >
                            <ToggleLeft size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Issue Items Dialog */}
      <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={24} className="text-green-600" />
              Issue Items - {selectedInternalReq?.requisition_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm"><span className="font-medium">Department:</span> {selectedInternalReq?.department}</p>
              <p className="text-sm"><span className="font-medium">Requester:</span> {selectedInternalReq?.requester_name}</p>
            </div>
            
            <div className="space-y-3">
              <Label className="font-medium">Items to Issue</Label>
              {issueItems.map((item, idx) => {
                const hasShortfall = item.quantity_issued < item.quantity_requested;
                return (
                  <div key={idx} className={`p-3 rounded-lg border ${hasShortfall ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{item.item_name}</span>
                      <span className="text-sm text-slate-500">
                        Requested: {item.quantity_requested} {item.unit} | In Stock: {item.current_stock || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Issue Qty:</Label>
                        <Input
                          type="number"
                          min="0"
                          max={item.current_stock || 0}
                          value={item.quantity_issued}
                          onChange={(e) => updateIssueQty(item.item_id, e.target.value)}
                          className="w-24"
                          data-testid={`issue-qty-${item.item_id}`}
                        />
                      </div>
                      {hasShortfall && (
                        <div className="flex-1">
                          <Input
                            placeholder="Shortfall note (required)"
                            value={item.shortfall_note}
                            onChange={(e) => updateShortfallNote(item.item_id, e.target.value)}
                            className="text-sm"
                            data-testid={`shortfall-note-${item.item_id}`}
                          />
                        </div>
                      )}
                    </div>
                    {hasShortfall && (
                      <p className="text-xs text-amber-600 mt-1">
                        <Warning size={12} className="inline mr-1" />
                        Shortfall: {item.quantity_requested - item.quantity_issued} {item.unit}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div>
              <Label className="font-medium">Issue Notes (Optional)</Label>
              <Input
                value={issueNotes}
                onChange={(e) => setIssueNotes(e.target.value)}
                placeholder="Add any notes about this issue..."
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIssueDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleIssueRequisition}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-issue-btn"
              >
                {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
                Confirm & Issue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Requisition</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to reject <span className="font-medium">{selectedInternalReq?.requisition_number}</span>?
            </p>
            <div>
              <Label className="font-medium">Rejection Reason *</Label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection..."
                className="mt-1"
                data-testid="reject-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleRejectRequisition}
                disabled={submitting || !rejectReason.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject-btn"
              >
                {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inventory Count Dialog */}
      <Dialog open={countDialogOpen} onOpenChange={setCountDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={24} className="text-[#8B5CF6]" />
              Physical Inventory Count
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Enter the physical count for each item. The system will automatically calculate variances.
            </p>
            
            {/* Count Items Table */}
            <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Item</th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700 w-28">Previous</th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700 w-32">Counted</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Variance Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {countItems.map((item, idx) => {
                    const variance = item.counted_quantity - item.previous_stock_level;
                    const hasVariance = variance !== 0;
                    return (
                      <tr key={idx} className={hasVariance ? 'bg-amber-50' : ''}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-800">{item.item_name}</span>
                          <br />
                          <span className="text-xs text-slate-500">{item.unit_of_measure}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">
                          {item.previous_stock_level.toFixed(1)}
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={item.counted_quantity}
                            onChange={(e) => updateCountedQuantity(idx, e.target.value)}
                            className={`w-24 text-center ${hasVariance ? 'border-amber-300' : ''}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {hasVariance ? (
                            <div>
                              <span className={`text-xs font-medium ${variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {variance > 0 ? '+' : ''}{variance.toFixed(1)}
                              </span>
                              <Input
                                value={item.variance_note}
                                onChange={(e) => updateVarianceNote(idx, e.target.value)}
                                placeholder="Explain variance"
                                className="mt-1 text-sm"
                              />
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Count Notes */}
            <div className="space-y-2">
              <Label>Count Notes (Optional)</Label>
              <textarea
                value={countNotes}
                onChange={(e) => setCountNotes(e.target.value)}
                placeholder="Any notes about this count..."
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none"
                rows={2}
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setCountDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitCount}
                disabled={submitting}
                className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
              >
                {submitting ? (
                  <Spinner size={18} className="animate-spin mr-2" />
                ) : (
                  <CheckCircle size={18} className="mr-2" />
                )}
                Submit Count
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog - Updated with 3-level hierarchy */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package size={20} className="text-[#8B5CF6]" />
                Add Item to Master List
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-3 space-y-3 max-h-[45vh] overflow-y-auto">
            <div className="space-y-1">
              <Label htmlFor="item_name" className="text-sm">Item Name *</Label>
              <Input
                id="item_name"
                value={newItem.item_name}
                onChange={(e) => setNewItem({...newItem, item_name: e.target.value})}
                placeholder="e.g., Olive Oil Extra Virgin"
                data-testid="new-item-name"
                className="h-9"
              />
            </div>
            
            {/* Location & Category in one row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-sm">Location *</Label>
                <select
                  value={newItem.location_id}
                  onChange={(e) => setNewItem({...newItem, location_id: e.target.value})}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                  data-testid="new-item-location"
                >
                  <option value="">Select</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Category *</Label>
                <select
                  value={newItem.category_id}
                  onChange={(e) => setNewItem({...newItem, category_id: e.target.value})}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                  disabled={!newItem.location_id}
                  data-testid="new-item-category"
                >
                  <option value="">{newItem.location_id ? 'Select' : 'Location first'}</option>
                  {filteredCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Subcategory & Cost Tag in one row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-sm">Subcategory</Label>
                <select
                  value={newItem.subcategory_id}
                  onChange={(e) => setNewItem({...newItem, subcategory_id: e.target.value})}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                  disabled={!newItem.category_id}
                  data-testid="new-item-subcategory"
                >
                  <option value="">None</option>
                  {filteredSubcategories.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Cost Tag *</Label>
                <select
                  value={newItem.cost_tag}
                  onChange={(e) => setNewItem({...newItem, cost_tag: e.target.value})}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                  data-testid="new-item-cost-tag"
                >
                  {costTags.map(tag => (
                    <option key={tag.code} value={tag.code.toLowerCase()}>{tag.name} ({tag.code})</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Unit & Par Level in one row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-sm">Unit</Label>
                <Input
                  value={newItem.unit_of_measure}
                  onChange={(e) => setNewItem({...newItem, unit_of_measure: e.target.value})}
                  placeholder="kg, liters"
                  data-testid="new-item-unit"
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Par Level</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={newItem.par_level}
                  onChange={(e) => setNewItem({...newItem, par_level: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                  data-testid="new-item-par-level"
                  className="h-8"
                />
              </div>
            </div>
            
            <p className="text-xs text-slate-500">
              SKU: <span className="font-mono text-[#8B5CF6]">{getCostTagPrefix(newItem.cost_tag)}-XXXX</span> (auto)
            </p>
          </div>
          
          {/* STICKY FOOTER: Save and Cancel Buttons - ALWAYS VISIBLE */}
          <div className="sticky bottom-0 flex justify-end gap-3 p-4 border-t border-slate-200 bg-white">
            <Button 
              variant="outline" 
              onClick={() => {
                setAddItemDialogOpen(false);
                setNewItem({
                  item_name: '',
                  location_id: '',
                  category_id: '',
                  subcategory_id: '',
                  cost_tag: 'food',
                  unit_of_measure: 'units',
                  par_level: 0,
                  description: ''
                });
              }}
              data-testid="cancel-new-item-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddItem}
              disabled={submitting || !newItem.item_name.trim() || !newItem.location_id || !newItem.category_id}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
              data-testid="save-new-item-btn"
            >
              {submitting ? (
                <Spinner size={18} className="animate-spin mr-2" />
              ) : (
                <CheckCircle size={18} className="mr-2" />
              )}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editItemDialogOpen} onOpenChange={setEditItemDialogOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PencilSimple size={20} className="text-[#8B5CF6]" />
                Edit Item
              </DialogTitle>
            </DialogHeader>
          </div>
          {editingItem && (
            <>
              <div className="px-6 py-3 space-y-3 max-h-[45vh] overflow-y-auto">
                {/* SKU Display (Read-only) */}
                <div className="bg-slate-50 p-2 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-slate-600">SKU:</span>
                  <span className="font-mono text-[#8B5CF6] font-bold">{editingItem.sku || 'LEGACY'}</span>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-sm">Item Name *</Label>
                  <Input
                    value={editingItem.item_name}
                    onChange={(e) => setEditingItem({...editingItem, item_name: e.target.value})}
                    placeholder="e.g., Olive Oil Extra Virgin"
                    data-testid="edit-item-name"
                    className="h-8"
                  />
                </div>
                
                {/* Location & Category in one row */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Location</Label>
                    <select
                      value={editingItem.location_id}
                      onChange={(e) => setEditingItem({...editingItem, location_id: e.target.value, category_id: '', subcategory_id: ''})}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                      data-testid="edit-item-location"
                    >
                      <option value="">Select</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Category</Label>
                    <select
                      value={editingItem.category_id}
                      onChange={(e) => setEditingItem({...editingItem, category_id: e.target.value, subcategory_id: ''})}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                      disabled={!editingItem.location_id}
                      data-testid="edit-item-category"
                    >
                      <option value="">Select</option>
                      {editFilteredCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Subcategory & Cost Tag in one row */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Subcategory</Label>
                    <select
                      value={editingItem.subcategory_id}
                      onChange={(e) => setEditingItem({...editingItem, subcategory_id: e.target.value})}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                      disabled={!editingItem.category_id}
                      data-testid="edit-item-subcategory"
                    >
                      <option value="">None</option>
                      {editFilteredSubcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>
                  {isPM && (
                    <div className="space-y-1">
                      <Label className="text-sm">Cost Tag</Label>
                      <select
                        value={editingItem.cost_tag}
                        onChange={(e) => setEditingItem({...editingItem, cost_tag: e.target.value})}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm"
                        data-testid="edit-item-cost-tag"
                      >
                        {costTags.map(tag => (
                          <option key={tag.code} value={tag.code.toLowerCase()}>{tag.name} ({tag.code})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                
                {/* Unit & Par Level in one row */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Unit</Label>
                    <Input
                      value={editingItem.unit_of_measure}
                      onChange={(e) => setEditingItem({...editingItem, unit_of_measure: e.target.value})}
                      placeholder="kg, liters"
                      data-testid="edit-item-unit"
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Par Level</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={editingItem.par_level}
                      onChange={(e) => setEditingItem({...editingItem, par_level: parseFloat(e.target.value) || 0})}
                      placeholder="0"
                      data-testid="edit-item-par-level"
                      className="h-8"
                    />
                  </div>
                </div>
                
                {/* Active Status Toggle - Compact */}
                <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-700">Active</span>
                  <button
                    type="button"
                    onClick={() => setEditingItem({...editingItem, is_active: !editingItem.is_active})}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${editingItem.is_active ? 'bg-[#8B5CF6]' : 'bg-slate-300'}`}
                    data-testid="edit-item-active-toggle"
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${editingItem.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
              
              {/* STICKY FOOTER: Update and Cancel Buttons - ALWAYS VISIBLE */}
              <div className="sticky bottom-0 flex justify-end gap-3 p-4 border-t border-slate-200 bg-white">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditItemDialogOpen(false);
                    setEditingItem(null);
                  }}
                  data-testid="cancel-edit-item-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditItem}
                  disabled={submitting || !editingItem.item_name?.trim()}
                  className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                  data-testid="update-item-btn"
                >
                  {submitting ? (
                    <Spinner size={18} className="animate-spin mr-2" />
                  ) : (
                    <CheckCircle size={18} className="mr-2" />
                  )}
                  Update
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Location Dialog */}
      <Dialog open={addLocationDialogOpen} onOpenChange={setAddLocationDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin size={20} className="text-[#8B5CF6]" />
              Add Store Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Location Name *</Label>
              <Input
                value={newLocation.name}
                onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                placeholder="e.g., Main Kitchen Store, Beverage Cellar"
                data-testid="new-location-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <textarea
                value={newLocation.description}
                onChange={(e) => setNewLocation({...newLocation, description: e.target.value})}
                placeholder="Describe this storage location..."
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAddLocationDialogOpen(false);
                  setNewLocation({ name: '', description: '' });
                }}
                data-testid="cancel-location-btn"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddLocation} 
                disabled={submitting || !newLocation.name.trim()} 
                className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                data-testid="save-location-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={addCategoryDialogOpen} onOpenChange={setAddCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderSimple size={20} className="text-amber-500" />
              Add Category
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Location *</Label>
              <select
                value={newCategory.location_id}
                onChange={(e) => setNewCategory({...newCategory, location_id: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                data-testid="new-category-location"
              >
                <option value="">Select Location</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                placeholder="e.g., Dry Goods, Beverages, Cleaning Supplies"
                data-testid="new-category-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                placeholder="Describe this category..."
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAddCategoryDialogOpen(false);
                  setNewCategory({ name: '', location_id: '', description: '' });
                }}
                data-testid="cancel-category-btn"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddCategory} 
                disabled={submitting || !newCategory.name.trim() || !newCategory.location_id} 
                className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                data-testid="save-category-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Subcategory Dialog */}
      <Dialog open={addSubcategoryDialogOpen} onOpenChange={setAddSubcategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={20} className="text-slate-500" />
              Add Subcategory
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <select
                value={newSubcategory.category_id}
                onChange={(e) => setNewSubcategory({...newSubcategory, category_id: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                data-testid="new-subcategory-category"
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.location_name} → {cat.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Subcategory Name *</Label>
              <Input
                value={newSubcategory.name}
                onChange={(e) => setNewSubcategory({...newSubcategory, name: e.target.value})}
                placeholder="e.g., Olive Oils, Red Wines, Floor Cleaners"
                data-testid="new-subcategory-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <textarea
                value={newSubcategory.description}
                onChange={(e) => setNewSubcategory({...newSubcategory, description: e.target.value})}
                placeholder="Describe this subcategory..."
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAddSubcategoryDialogOpen(false);
                  setNewSubcategory({ name: '', category_id: '', description: '' });
                }}
                data-testid="cancel-subcategory-btn"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddSubcategory} 
                disabled={submitting || !newSubcategory.name.trim() || !newSubcategory.category_id} 
                className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                data-testid="save-subcategory-btn"
              >
                {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Custom Cost Tag Dialog */}
      <Dialog open={addCostTagDialogOpen} onOpenChange={setAddCostTagDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} className="text-[#8B5CF6]" />
              Add Custom Cost Tag
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="cost_tag_name">Name *</Label>
              <Input
                id="cost_tag_name"
                value={newCostTag.name}
                onChange={(e) => setNewCostTag({...newCostTag, name: e.target.value})}
                placeholder="e.g., Gift Shop"
                data-testid="new-cost-tag-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cost_tag_code">Two-Letter Code *</Label>
              <Input
                id="cost_tag_code"
                value={newCostTag.code}
                onChange={(e) => setNewCostTag({...newCostTag, code: e.target.value.toUpperCase().slice(0, 2)})}
                placeholder="e.g., GS"
                maxLength={2}
                className="uppercase"
                data-testid="new-cost-tag-code"
              />
              <p className="text-xs text-slate-500">
                This code becomes the SKU prefix (e.g., <span className="font-mono text-[#8B5CF6]">{newCostTag.code || 'XX'}-0001</span>)
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button 
              variant="outline" 
              onClick={() => {
                setAddCostTagDialogOpen(false);
                setNewCostTag({ name: '', code: '' });
              }}
              data-testid="cancel-cost-tag-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCostTag}
              disabled={submitting || !newCostTag.name.trim() || newCostTag.code.length !== 2}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
              data-testid="save-cost-tag-btn"
            >
              {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Custom Cost Tag Dialog */}
      <Dialog open={editCostTagDialogOpen} onOpenChange={setEditCostTagDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={20} className="text-[#8B5CF6]" />
              Edit Cost Tag
            </DialogTitle>
          </DialogHeader>
          {editingCostTag && (
            <div className="space-y-4 pt-4">
              <div className="bg-slate-50 p-3 rounded-lg flex items-center justify-between">
                <span className="text-sm text-slate-600">Code:</span>
                <span className="font-mono text-[#8B5CF6] font-bold">{editingCostTag.code}</span>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_cost_tag_name">Name</Label>
                <Input
                  id="edit_cost_tag_name"
                  value={editingCostTag.name}
                  onChange={(e) => setEditingCostTag({...editingCostTag, name: e.target.value})}
                  placeholder="e.g., Gift Shop"
                  data-testid="edit-cost-tag-name"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button 
              variant="outline" 
              onClick={() => {
                setEditCostTagDialogOpen(false);
                setEditingCostTag(null);
              }}
              data-testid="cancel-edit-cost-tag-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCostTag}
              disabled={submitting || !editingCostTag?.name?.trim()}
              className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
              data-testid="update-cost-tag-btn"
            >
              {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
              Update
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reports Card - At the bottom of Storekeeper Dashboard */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="reports-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center">
            <ChartBar size={28} className="text-[#3D1D72]" weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800">Reports</h3>
            <p className="text-sm text-slate-500">View Low Stock Report for reorder planning</p>
          </div>
          <Button 
            onClick={() => window.location.href = '/reports'}
            className="bg-[#3D1D72] hover:bg-[#2D1552]"
            data-testid="open-reports-btn"
          >
            Open Reports
          </Button>
        </div>
      </div>

      {/* Replenishment Requisition Dialog */}
      <Dialog open={replenishDialogOpen} onOpenChange={setReplenishDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Replenishment Requisition</DialogTitle>
          </DialogHeader>
          {replenishItem && (
            <div className="space-y-4 mt-4">
              {/* Pre-populated item info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-slate-500">Item Name</span>
                  <span className="font-medium text-slate-800" data-testid="replenish-item-name">{replenishItem.item_name}</span>
                </div>
                {replenishItem.sku && <div className="flex justify-between">
                  <span className="text-xs text-slate-500">SKU</span>
                  <span className="text-slate-600">{replenishItem.sku}</span>
                </div>}
                <div className="flex justify-between">
                  <span className="text-xs text-slate-500">Unit</span>
                  <span className="text-slate-600">{replenishItem.unit_of_measure}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-slate-500">Current Stock</span>
                  <span className="font-medium text-red-600" data-testid="replenish-current-stock">{replenishItem.current_stock_level || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-slate-500">Par Level</span>
                  <span className="font-medium text-slate-800" data-testid="replenish-par-level">{replenishItem.par_level || 0}</span>
                </div>
              </div>

              {/* Editable fields */}
              <div className="space-y-2">
                <Label>Quantity to Replenish <span className="text-red-500">*</span></Label>
                <Input type="number" min="1" step="1" value={replenishQty}
                  onChange={e => setReplenishQty(e.target.value)}
                  data-testid="replenish-qty-input" />
                <p className="text-xs text-slate-500">Suggested: {Math.max(0, (replenishItem.par_level || 0) - (replenishItem.current_stock_level || 0))} {replenishItem.unit_of_measure}</p>
              </div>

              <div className="space-y-2">
                <Label>Urgency Level</Label>
                <select value={replenishUrgency} onChange={e => setReplenishUrgency(e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                  data-testid="replenish-urgency-select">
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <textarea value={replenishNotes} onChange={e => setReplenishNotes(e.target.value)}
                  placeholder="Any additional information for the PM..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none" rows={2}
                  data-testid="replenish-notes-input" />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setReplenishDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmitReplenishment} disabled={submitting}
                  className="bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="submit-replenish-btn">
                  {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                  Submit Requisition
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorekeeperPortalPage;
